<template>
  <Modal :show="show" @close-modal="$emit('close-spv-options')">
    <div class="spv-options">
      <div class="success-icon">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M26.6667 8L12 22.6667L5.33334 16" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <h1 class="spv-options__title">Deal request submitted</h1>
      <p class="spv-options__subtitle">
        Is there an SPV that will be linked to the deal you created?
      </p>

      <div class="spv-options__list">
        <div
          class="option-card"
          :class="{ 'option-card--active': spvOption === 'old' }"
          @click="spvOption = 'old'"
        >
          <div class="option-card__radio">
            <div v-if="spvOption === 'old'" class="option-card__radio-inner"></div>
          </div>
          <div class="option-card__content">
            <span class="option-card__label">Yes, I have an SPV</span>
            <span class="option-card__desc">Upload your existing SPV documents</span>
          </div>
        </div>

        <router-link
          class="option-card"
          :class="{ 'option-card--active': spvOption === 'new' }"
          :to="{ name: 'CreateNewSpv', params: { id } }"
          @click="spvOption = 'new'"
        >
          <div class="option-card__radio">
            <div v-if="spvOption === 'new'" class="option-card__radio-inner"></div>
          </div>
          <div class="option-card__content">
            <span class="option-card__label">No, I want to create an SPV</span>
            <span class="option-card__desc">Set up a new Special Purpose Vehicle</span>
          </div>
        </router-link>
      </div>

      <button class="continue-btn" @click="setOption" :disabled="!spvOption">
        Continue
      </button>

      <router-link
        class="skip-link"
        :to="{ name: 'PrivateRaiseInfo', params: { id } }"
      >
        Skip for now
      </router-link>
    </div>
  </Modal>
</template>

<script>
  import Modal from "@/components/general/Modal";
  
  export default {
    name: "SpvOptions",
    
    components: {
      Modal,
    },
    
    props: {
      show: {
        type: Boolean,
        required: true,
      },
      id: {
        type: String,
        required: true,
      },
    },
    
    emits: ["close-spv-options"],
    
    data() {
      return {
        spvOption: null,
      };
    },
    
    methods: {
      setOption() {
        const spvOption = this.spvOption;
        switch (spvOption) {
          case "old":
            this.$router.push({
              name: "SpvUpload",
              params: { id: this.id },
            });
            break;
          case "new":
            this.$router.push({
              name: "CreateNewSpv",
              params: { id: this.id },
            });
            break;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
.spv-options {
  padding: 32px;
  text-align: center;
  max-width: 420px;

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0 0 24px 0;
    line-height: 1.5;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
  }
}

.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #10B981 0%, #34D399 100%);
  border-radius: 50%;
  color: white;
  margin: 0 auto 20px;
}

.option-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  cursor: pointer;
  text-decoration: none;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    border-color: #CBD5E1;
    background: white;
  }

  &--active {
    border-color: #4D84FF;
    background: rgba(77, 132, 255, 0.04);

    .option-card__radio {
      border-color: #4D84FF;
    }
  }

  &__radio {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    background: white;
    border: 2px solid #CBD5E1;
    border-radius: 50%;
    margin-top: 2px;
    transition: border-color 0.2s ease;
  }

  &__radio-inner {
    width: 10px;
    height: 10px;
    background: #4D84FF;
    border-radius: 50%;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__label {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
  }

  &__desc {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #64748B;
  }
}

.continue-btn {
  width: 100%;
  padding: 14px 24px;
  background: #4D84FF;
  border: none;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #3B6EE8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.skip-link {
  display: inline-block;
  margin-top: 20px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #64748B;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #4D84FF;
  }
}
</style>
